import API_CONFIG from '../config/api.config';
import apiClient from './apiClient';

// ── Mock delay helper ─────────────────────────────────────────
const delay = (ms = API_CONFIG.MOCK_DELAY) => new Promise(r => setTimeout(r, ms));

// ── Mock users ────────────────────────────────────────────────
const MOCK_USERS = {
  // .com = used by AuthModal demo buttons + AuthContext
  'mentor@devbrains.com': {
    id: 'mock_mentor_001', firstName: 'Amar', lastName: 'Krishnan',
    name: 'Amar Krishnan', email: 'mentor@devbrains.com', role: 'mentor',
    avatarBg: '16a34a', initials: 'AK', headline: 'Senior Software Engineer | Ex-Google',
    bio: 'Ex-Google engineer with 8 years of experience.', location: 'Bengaluru',
    timezone: 'IST (UTC+5:30)', isApproved: true, isEmailVerified: true, profileComplete: 87,
    joinedDate: 'Mar 2024',
  },
  'mentee@devbrains.com': {
    id: 'mock_mentee_001', firstName: 'Rahul', lastName: 'Verma',
    name: 'Rahul Verma', email: 'mentee@devbrains.com', role: 'mentee',
    avatarBg: '2563eb', initials: 'RV', headline: 'Aspiring Full-Stack Developer',
    bio: 'CS graduate building real projects.', location: 'Mumbai',
    timezone: 'IST (UTC+5:30)', isApproved: true, isEmailVerified: true, profileComplete: 65,
    joinedDate: 'Jan 2025',
  },
  'admin@devbrains.com': {
    id: 'mock_admin_001', firstName: 'Admin', lastName: 'DevBrains',
    name: 'Admin DevBrains', email: 'admin@devbrains.com', role: 'admin',
    avatarBg: '7c3aed', initials: 'AD', headline: 'Platform Administrator',
    bio: '', location: 'Remote', timezone: 'IST (UTC+5:30)', isApproved: true,
    isEmailVerified: true, profileComplete: 100, joinedDate: 'Jun 2023',
  },
  // .in aliases — used by seed.js & API mode demo
  'mentor@devbrains.in':  { alias: 'mentor@devbrains.com' },
  'mentee@devbrains.in':  { alias: 'mentee@devbrains.com' },
  'admin@devbrains.in':   { alias: 'admin@devbrains.com'  },
};

function resolveMockUser(email) {
  const e = email.toLowerCase();
  const u = MOCK_USERS[e];
  if (!u) return null;
  if (u.alias) return MOCK_USERS[u.alias] || null;
  return u;
}

// ═══════════════════════════════════════════════════════════════
//  AUTH SERVICE
// ═══════════════════════════════════════════════════════════════
const authService = {

  // ── Login ──────────────────────────────────────────────────
  async login(email, password) {
    if (API_CONFIG.MOCK) {
      await delay();
      const user = resolveMockUser(email);
      const validPass = { mentor: 'mentor123', mentee: 'mentee123', admin: 'admin123' };
      if (!user || (password !== 'password123' && password !== validPass[user.role])) {
        const e = new Error('Invalid credentials. Use mentor123 / mentee123 / admin123'); e.code = 'VALIDATION_ERROR'; throw e;
      }
      const tokens = { accessToken: `mock_access_${user.id}`, refreshToken: `mock_refresh_${user.id}` };
      localStorage.setItem('accessToken',  tokens.accessToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);
      return { user: { ...user, isEmailVerified: true }, ...tokens };
    }
    // Real API
    const data = await apiClient.post('/auth/login', { email, password });
    localStorage.setItem('accessToken',  data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    return data;
  },

  // ── Register ───────────────────────────────────────────────
  async register({ firstName, lastName, email, password, role }) {
    if (API_CONFIG.MOCK) {
      await delay();
      const user = { id: `mock_${Date.now()}`, firstName, lastName, email, role, isEmailVerified: false };
      const tokens = { accessToken: `mock_access_${user.id}`, refreshToken: `mock_refresh_${user.id}` };
      localStorage.setItem('accessToken',  tokens.accessToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);
      return { user, ...tokens, message: '[MOCK] Registration successful.' };
    }
    const data = await apiClient.post('/auth/register', {
      firstName, lastName, email, password, role,
      termsAccepted: 'true', termsVersion: '2.1', privacyVersion: '1.4',
    });
    localStorage.setItem('accessToken',  data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);
    return data;
  },

  // ── Logout ─────────────────────────────────────────────────
  async logout() {
    if (!API_CONFIG.MOCK) {
      try { await apiClient.post('/auth/logout'); } catch {}
    }
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  },

  // ── Get current user ───────────────────────────────────────
  async getMe() {
    if (API_CONFIG.MOCK) {
      await delay();
      const token = localStorage.getItem('accessToken') || '';
      const userId = token.replace('mock_access_', '');
      const user   = Object.values(MOCK_USERS).filter(u => u.id).find(u => u.id === userId);
      if (!user) { const e = new Error('Not authenticated.'); e.code = 'NOT_AUTHENTICATED'; throw e; }
      return { user };
    }
    return apiClient.get('/users/me');
  },

  // ── Verify email (token from link) ────────────────────────
  async verifyEmail(token) {
    if (API_CONFIG.MOCK) {
      await delay();
      return { message: '[MOCK] Email verified.' };
    }
    return apiClient.post('/auth/verify-email', { token });
  },

  // ── Resend verification email ──────────────────────────────
  async resendVerification(email) {
    if (API_CONFIG.MOCK) {
      await delay();
      return { message: '[MOCK] Verification email resent to ' + email };
    }
    return apiClient.post('/auth/resend-verification', { email });
  },

  // ── Forgot password ────────────────────────────────────────
  async forgotPassword(email, redirectUrl) {
    if (API_CONFIG.MOCK) {
      await delay();
      return { message: '[MOCK] Reset email sent to ' + email };
    }
    return apiClient.post('/auth/forgot-password', { email, redirectUrl });
  },

  // ── Reset password ─────────────────────────────────────────
  async resetPassword(token, newPassword) {
    if (API_CONFIG.MOCK) {
      await delay();
      return { message: '[MOCK] Password reset successfully.' };
    }
    return apiClient.post('/auth/reset-password', { token, newPassword });
  },

  // ── Social login ───────────────────────────────────────────
  async socialLogin({ provider, email, name, role }) {
    if (API_CONFIG.MOCK) {
      await delay();
      const [firstName, ...rest] = (name || 'User').split(' ');
      const user = { id: `mock_social_${Date.now()}`, firstName, lastName: rest.join(' ') || '', email, role: role || 'mentee' };
      const tokens = { accessToken: `mock_access_${user.id}`, refreshToken: `mock_refresh_${user.id}` };
      localStorage.setItem('accessToken',  tokens.accessToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);
      return { user, ...tokens, isNew: true };
    }
    return apiClient.post('/auth/social', { provider, email, name, role });
  },
};

export default authService;
